// src/components/profile/NicknameForm.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { contentUsersDatabase } from '../../firebase';
import shared from '../../styles/shared.module.css';
import styles from './Profile.module.css';

const ProfileForm = ({ isModal = false, onClose = null, onProfileUpdated = null }) => {
  const { currentUser, getUserProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [nickname, setNickname] = useState('');
  const [company, setCompany] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [interests, setInterests] = useState('');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const userProfile = await getUserProfile(currentUser.uid);
        setProfile(userProfile);
        setLastName(userProfile?.lastName || '');
        setFirstName(userProfile?.firstName || '');
        setNickname(userProfile?.nickname || '');
        setCompany(userProfile?.company || '');
        setJobDescription(userProfile?.jobDescription || '');
        setInterests(userProfile?.interests || '');
      } catch (err) {
        console.error('プロフィール読み込みエラー:', err);
        setError('プロフィールの読み込み中にエラーが発生しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, getUserProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lastName.trim()) {
      setError('姓を入力してください。');
      return;
    }
    if (!firstName.trim()) {
      setError('名を入力してください。');
      return;
    }
    if (!nickname.trim()) {
      setError('ニックネームを入力してください。');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const updatedProfile = { 
        ...profile, 
        lastName: lastName.trim(),
        firstName: firstName.trim(),
        nickname: nickname.trim(),
        company: company.trim(),
        jobDescription: jobDescription.trim(),
        interests: interests.trim(),
        updatedAt: new Date().toISOString()
      };
      
      console.log('プロフィールを更新中:', updatedProfile);
      await updateUserProfile(currentUser.uid, updatedProfile);

      // Content DB にもプロフィールを同期（Bar画面で使用するため）
      try {
        const contentProfileRef = ref(contentUsersDatabase, `users/profiles/${currentUser.uid}`);
        const contentSnapshot = await get(contentProfileRef);
        const existingContent = contentSnapshot.exists() ? contentSnapshot.val() : null;
        const timestamp = new Date().toISOString();
        const contentUpdates = {
          displayName: updatedProfile.nickname || (existingContent?.displayName ?? ''),
          lastName: (updatedProfile.lastName ?? existingContent?.lastName) ?? '',
          firstName: (updatedProfile.firstName ?? existingContent?.firstName) ?? '',
          email: (updatedProfile.email || currentUser.email || existingContent?.email) ?? '',
          company: (updatedProfile.company ?? existingContent?.company) ?? '',
          jobDescription: (updatedProfile.jobDescription ?? existingContent?.jobDescription) ?? '',
          interests: (updatedProfile.interests ?? existingContent?.interests) ?? '',
          updatedAt: timestamp
        };
        if (!existingContent) {
          contentUpdates.paymentInfo = { currentStatus: profile?.paymentStatus ?? 'unpaid' };
          contentUpdates.createdAt = timestamp;
        }
        await update(contentProfileRef, contentUpdates);
      } catch (contentErr) {
        console.warn('Content DBへのプロフィール同期はスキップしました:', contentErr);
      }

      if (onProfileUpdated) onProfileUpdated(true); // true = 保存後の再取得のためモーダルを開かない
      setSuccess('プロフィールを更新しました。');
      
      if (onClose && isModal) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('プロフィール更新エラー:', err);
      setError('プロフィールの更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onClose && isModal) {
      onClose();
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className={`${shared.loadingSpinner} ${isModal ? styles.modalContainer : ''}`}>
        <div className={shared.spinner}></div>
      </div>
    );
  }

  return (
    <div className={isModal ? styles.modalContainer : shared.pageContainerWithPadding}>
      <div className={styles.formContainer}>
        <h2 className={styles.title}>プロフィール設定</h2>

        {error && (
          <div className={shared.errorMessage}>
            {error}
          </div>
        )}

        {success && (
          <div className={shared.successMessage}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 本名（姓・名）入力 */}
          <div className={styles.formRow}>
            <div className={shared.formGroup}>
              <label className={shared.label}>姓 <span className={styles.required}>*</span></label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={shared.input}
                placeholder="姓を入力"
                required
              />
            </div>
            <div className={shared.formGroup}>
              <label className={shared.label}>名 <span className={styles.required}>*</span></label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={shared.input}
                placeholder="名を入力"
                required
              />
            </div>
          </div>

          {/* ニックネーム入力 */}
          <div className={shared.formGroup}>
            <label className={shared.label}>
              ニックネーム <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className={shared.input}
              placeholder="ニックネームを入力"
              required
            />
            <p className={shared.helperText}>
              店内でのオーダー時に使用されます
            </p>
          </div>

          {/* 会社名入力 */}
          <div className={shared.formGroup}>
            <label className={shared.label}>
              会社名
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className={shared.input}
              placeholder="会社名を入力"
            />
          </div>

          {/* 仕事内容入力 */}
          <div className={shared.formGroup}>
            <label className={shared.label}>
              仕事内容
            </label>
            <input
              type="text"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              className={shared.input}
              placeholder="仕事内容を入力"
            />
          </div>

          {/* 社内でやってみたいこと入力 */}
          <div className={shared.formGroup}>
            <label className={shared.label}>
              社内でやってみたいこと
            </label>
            <textarea
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              className={shared.textarea}
              placeholder="社内でやってみたいことを入力"
              rows="3"
            ></textarea>
          </div>

          <div className={shared.buttonContainer}>
            <button
              type="submit"
              disabled={saving}
              className={shared.saveButton}
            >
              {saving ? '保存中...' : '保存'}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className={shared.cancelButton}
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileForm;